import { Autocomplete, AutocompleteOption, ListItemContent, Stack, Typography, VariantProp } from '@healthinal/ui';
import { matchSorter } from 'match-sorter';
import { useMemo, useState } from 'react';
import { ReferencePersonDto } from '../../../api/generated/model/bezugspersonDto';
import type { ReferencePersonCreateEndpoint } from './CreateReferencePersonDialog';
import { CreateReferencePersonDialog } from './CreateReferencePersonDialog';
import { ReferencePersonDisplay } from './ReferencePersonDisplay';

type BaseProps = {
  value: ReferencePersonDto[];
  onChange: (value: ReferencePersonDto[]) => void;
  variant?: VariantProp;
  disabled?: boolean;
};

type CreateModeProps = BaseProps & {
  mode: 'create';
  fetchedOptions?: ReferencePersonDto[];
};

type EditModeProps = BaseProps & {
  mode: 'edit';
  personId: string;
  createEndpoint?: ReferencePersonCreateEndpoint;
  fetchedOptions: ReferencePersonDto[];
};

type Props = CreateModeProps | EditModeProps;

const CREATE_OPTION_ID = '__create_reference_person__';

type CreateOption = {
  id: typeof CREATE_OPTION_ID;
  firstName: string;
  lastName: string;
  position?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
};

type ReferencePersonOption = ReferencePersonDto | CreateOption;

const CREATE_OPTION: CreateOption = {
  id: CREATE_OPTION_ID,
  firstName: 'Bezugsperson erstellen',
  lastName: '',
};

function isCreateOption(option: ReferencePersonOption): option is CreateOption {
  return option.id === CREATE_OPTION_ID;
}

function getReferencePersonKey(person: ReferencePersonDto) {
  return person.id ?? [person.firstName, person.lastName, person.position, person.phoneNumber, person.email].join('|');
}

function mergeUniqueReferencePersons(...groups: ReferencePersonDto[][]) {
  const optionsByKey = new Map<string, ReferencePersonDto>();

  groups.flat().forEach((person) => {
    optionsByKey.set(getReferencePersonKey(person), person);
  });

  return Array.from(optionsByKey.values());
}

function getSelectedReferencePersons(referencePersons: ReferencePersonDto[]) {
  return referencePersons.filter((person) => person.isSelected);
}

function applyReferencePersonSelection(
  referencePersons: ReferencePersonDto[],
  selectedReferencePersons: ReferencePersonDto[],
) {
  const selectedKeys = new Set(selectedReferencePersons.map(getReferencePersonKey));

  return mergeUniqueReferencePersons(referencePersons, selectedReferencePersons).map((person) => ({
    ...person,
    isSelected: selectedKeys.has(getReferencePersonKey(person)),
  }));
}

export function SelectReferencePersonMulti(props: Props) {
  const { value, onChange, variant, disabled = false } = props;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createdOptions, setCreatedOptions] = useState<ReferencePersonDto[]>([]);

  const sourceOptions = props.fetchedOptions ?? [];
  const selectedValue = useMemo(() => getSelectedReferencePersons(value), [value]);
  const referencePersonOptions = useMemo(
    () => mergeUniqueReferencePersons(sourceOptions, createdOptions, value),
    [sourceOptions, createdOptions, value],
  );

  const options = useMemo<ReferencePersonOption[]>(() => {
    return [...referencePersonOptions, CREATE_OPTION];
  }, [referencePersonOptions]);

  const handleCreatedReferencePerson = (referencePerson?: ReferencePersonDto) => {
    setIsCreateDialogOpen(false);

    if (!referencePerson) return;

    const selectedReferencePerson = { ...referencePerson, isSelected: true };

    setCreatedOptions((prev) => {
      const alreadyExists = prev.some(
        (item) => getReferencePersonKey(item) === getReferencePersonKey(selectedReferencePerson),
      );
      return alreadyExists ? prev : [...prev, selectedReferencePerson];
    });

    onChange(
      applyReferencePersonSelection(
        [...referencePersonOptions, selectedReferencePerson],
        [...selectedValue, selectedReferencePerson],
      ),
    );
  };

  return (
    <>
      <Stack gap={1}>
        <Autocomplete
          disabled={disabled}
          multiple
          value={selectedValue}
          options={options}
          variant={variant}
          onChange={(_, newValue) => {
            const nextValue = newValue ?? [];
            const hasCreateOption = nextValue.some(isCreateOption);

            if (hasCreateOption) {
              setIsCreateDialogOpen(true);
              return;
            }

            onChange(applyReferencePersonSelection(referencePersonOptions, nextValue as ReferencePersonDto[]));
          }}
          getOptionLabel={(option) => {
            if (isCreateOption(option)) {
              return 'Bezugsperson erstellen';
            }

            return `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim();
          }}
          isOptionEqualToValue={(option, selectedValue) =>
            !isCreateOption(option) &&
            !isCreateOption(selectedValue) &&
            getReferencePersonKey(option) === getReferencePersonKey(selectedValue)
          }
          filterOptions={(allOptions, { inputValue }): ReferencePersonOption[] => {
            const realOptions = allOptions.filter((item) => !isCreateOption(item));

            const filtered = matchSorter(realOptions, inputValue, {
              keys: ['firstName', 'lastName', 'position', 'phoneNumber', 'email'],
            });

            return [...filtered, CREATE_OPTION];
          }}
          renderOption={(props, option) => {
            if (isCreateOption(option)) {
              return (
                <AutocompleteOption {...props} key={CREATE_OPTION_ID} onClick={() => setIsCreateDialogOpen(true)}>
                  <Typography level="title-md">Bezugsperson erstellen</Typography>
                </AutocompleteOption>
              );
            }

            return (
              <AutocompleteOption {...props} key={getReferencePersonKey(option)}>
                <ListItemContent>
                  <Typography level="title-sm">
                    {option.firstName} {option.lastName}
                    {option.position ? ` (${option.position})` : ''}
                  </Typography>

                  {option.email ? <Typography level="body-sm">{option.email}</Typography> : null}

                  {option.phoneNumber ? <Typography level="body-sm">{option.phoneNumber}</Typography> : null}
                </ListItemContent>
              </AutocompleteOption>
            );
          }}
        />

        {selectedValue.length > 0 ? (
          <Stack sx={{ pl: 1.5 }} gap={1}>
            {selectedValue.map((person) => (
              <ReferencePersonDisplay key={getReferencePersonKey(person)} person={person} />
            ))}
          </Stack>
        ) : null}
      </Stack>

      {props.mode === 'edit' ? (
        <CreateReferencePersonDialog
          mode="edit"
          open={isCreateDialogOpen}
          personId={props.personId}
          createEndpoint={props.createEndpoint}
          onClose={handleCreatedReferencePerson}
        />
      ) : (
        <CreateReferencePersonDialog mode="create" open={isCreateDialogOpen} onClose={handleCreatedReferencePerson} />
      )}
    </>
  );
}
